/**
 * Clients → Emails — prototype `VIEWS.emails` + `mailTable` + `openMail` + `mailNotTask` + `propReview` /
 * `propConfirm` / `propReject` (docs/prototype.html L1308–1317, L2340, L2368, L2531–2588), as amended by the Step 8
 * decisions (#9, #14): there is no track@ address and no message bodies. Every member's own Gmail is read,
 * read-only and only for client domains; we keep the subject, who it was with and a ≤ 300-character snippet.
 *
 * Data: GET /email-threads (stats, my_gmail, members_not_connected, source_last_success_at), PATCH /email-threads/{id}
 * (assign · Not a task), POST /email-threads/{id}/task, GET /proposed-items?thread_id + confirm / reject,
 * POST /clients/{id}/email-domains (the domain offer, managers), GET /auth/google/connect (Connect your email).
 * Tone: the server sends the reason and quote to the founder and the associate director only, and the label to a
 * member only while that setting is on — this screen renders exactly what arrives.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  DROP_REASON_LABEL,
  EMAIL_STATE_LABEL,
  GOOGLE_CONNECT_ERROR_MESSAGE,
  GoogleConnectError,
  type ClientRef,
  type DropReason,
  type EmailThread,
  type GoogleConnectionState,
  type ProposedItem,
  type ThreadTone,
} from '@amplispot/contracts';
import { newIdempotencyKey } from '../../api';
import { useUsers } from '../../lib/data';
import { firstName, plural, todayIso } from '../../lib/format';
import {
  agoLabel,
  gmailConnectHref,
  MAIL_STALE_MS,
  STATE_CHIP,
  TONE_CHIP,
  useAddDomains,
  useEmailThread,
  useConfirmProposal,
  useEmailThreads,
  useMakeThreadTask,
  useRejectProposal,
  useThreadProposals,
  useUpdateThread,
  whenLabel,
  whenWithTime,
  type MailFilter,
} from '../../lib/mail';
import { useSession } from '../../lib/session';
import { DisconnectModal } from '../settings/Connections';
import { Avatar, Drawer, Empty, ErrorState, Field, Loading, Modal, Pills, errorText, useFail, useToast } from '../../ui/kit';
import { Stat } from '../timesheets/TimesheetReport';

const FILTERS: MailFilter[] = ['all', 'needs', 'waiting', 'replied', 'unmatched'];
const isFilter = (v: string | null): v is MailFilter => !!v && (FILTERS as string[]).includes(v);

/** The read-only disclosure (ADR-003 §10) that replaces the prototype's track@ note. */
const READ_ONLY_COPY =
  'We read only threads involving a client’s email domain, from the inboxes of members who have connected their account. We store the subject, who it was with and a short snippet — never the message body. No open-tracking pixels.';

type Open = { kind: 'thread'; id: string; snap: EmailThread } | { kind: 'notask'; thread: EmailThread } | { kind: 'proposal'; item: ProposedItem; thread: EmailThread } | null;

export function Emails() {
  const { can, readOnly, me } = useSession();
  const [disconnecting, setDisconnecting] = useState(false);
  const [sp, setSp] = useSearchParams();
  const f: MailFilter = isFilter(sp.get('f')) ? (sp.get('f') as MailFilter) : 'all';
  const setF = (v: MailFilter) =>
    setSp(
      (p) => {
        if (v === 'all') p.delete('f');
        else p.set('f', v);
        return p;
      },
      { replace: true },
    );
  const list = useEmailThreads(f);
  const [open, setOpen] = useState<Open>(null);
  const googleError = sp.get('google_error');
  const today = todayIso();

  // The open thread (drawer, Not a task, a proposal) is always the LIVE row: from the list while it is there, else
  // fetched by id (an Assign moves it off the Not matched list), so its client and version are current and a retry
  // never sends a stale If-Match. The snapshot only fills the moment before the fetch lands.
  const openId = open ? (open.kind === 'thread' ? open.id : open.thread.id) : null;
  const inList = openId ? list.threads.find((t) => t.id === openId) : undefined;
  const fetched = useEmailThread(openId, !inList);
  const live = (snap: EmailThread): EmailThread => inList ?? (fetched.data?.id === snap.id ? fetched.data : undefined) ?? snap;
  const shownThread = open?.kind === 'thread' ? live(open.snap) : null;

  // Deep link ?thread=<id>&proposal=<id> (Review queue, Work, Today): open that thread, and when the proposal is
  // still pending and this person can decide it, its Confirm / Reject straight away; otherwise the thread's drawer,
  // which shows what became of it. The params are dropped once used, so closing returns to the list.
  const deepThread = sp.get('thread');
  const deepProposal = sp.get('proposal');
  const deepT = useEmailThread(deepThread, true);
  const deepP = useThreadProposals(deepProposal ? deepThread : null);
  const [deepMiss, setDeepMiss] = useState<string | null>(null);
  useEffect(() => {
    if (!deepThread) return;
    const clear = () =>
      setSp(
        (x) => {
          x.delete('thread');
          x.delete('proposal');
          return x;
        },
        { replace: true },
      );
    if (deepT.error) {
      setDeepMiss(`That email could not be opened: ${errorText(deepT.error)}`);
      return clear();
    }
    const t = deepT.data;
    if (!t || (deepProposal && deepP.isPending)) return;
    const item = deepP.data?.data.find((x) => x.id === deepProposal);
    setOpen(item && item.status === 'pending' && item.can_decide && !readOnly ? { kind: 'proposal', item, thread: t } : { kind: 'thread', id: t.id, snap: t });
    setDeepMiss(null);
    clear();
  }, [deepThread, deepProposal, deepT.data, deepT.error, deepP.isPending, deepP.data, readOnly, setSp]);

  if (list.isLoading) return <Loading label="Loading emails…" />;
  if (list.error && !list.page) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  const pg = list.page!;
  const stale = pg.source_last_success_at && Date.now() - new Date(pg.source_last_success_at).getTime() > MAIL_STALE_MS;

  return (
    <>
      {deepThread ? (
        <div className="note" role="status" style={{ marginBottom: 12 }}>
          Opening that email…
        </div>
      ) : deepMiss ? (
        <div className="note warn" role="alert" style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ flex: 1 }}>{deepMiss}</span>
          <button className="btn sm ghost" onClick={() => setDeepMiss(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {googleError ? (
        <div className="note bad" role="alert" style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
          <span className="grow" style={{ flex: 1 }}>
            {GoogleConnectError.safeParse(googleError).success
              ? GOOGLE_CONNECT_ERROR_MESSAGE[googleError as GoogleConnectError]
              : 'Connecting your email did not finish. Press Connect to try again.'}
          </span>
          <button
            className="btn sm ghost"
            onClick={() =>
              setSp(
                (p) => {
                  p.delete('google_error');
                  return p;
                },
                { replace: true },
              )
            }
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {pg.my_gmail !== 'connected' ? <ConnectPanel state={pg.my_gmail} readOnly={readOnly || !!me.acting_as} /> : null}

      <div className="note" style={{ marginBottom: 16 }}>
        {READ_ONLY_COPY}
        {/* Members have no Settings, so their own connection state and Disconnect live here (managers: Settings → My connections). */}
        {pg.my_gmail === 'connected' && !can('clients.read.all') && !me.acting_as ? (
          <>
            {' '}Your Gmail is connected.{' '}
            <button type="button" className="lnk" style={{ color: 'var(--brand)', fontWeight: 600 }} onClick={() => setDisconnecting(true)}>
              Disconnect
            </button>
          </>
        ) : null}
      </div>
      {disconnecting ? <DisconnectModal scope="gmail" onClose={() => setDisconnecting(false)} /> : null}

      {pg.members_not_connected ? (
        <div className="note warn" style={{ marginBottom: 12 }}>
          <b>{plural(pg.members_not_connected, 'member')}</b> {pg.members_not_connected === 1 ? 'has' : 'have'} not connected email, so threads from
          their clients are missing here.
        </div>
      ) : null}
      {stale ? (
        <div className="note warn" style={{ marginBottom: 12 }}>
          Email was last read {agoLabel(pg.source_last_success_at!)}. New emails may be missing until the next read succeeds.
        </div>
      ) : null}

      <div className="stats">
        <Stat k="Tracked threads" v={pg.stats.tracked} />
        <Stat k="Waiting on clients" v={pg.stats.waiting} cls="amber" />
        <Stat k="Need a reply from us" v={pg.stats.needs} cls="red" />
        <Stat k="Replied" v={pg.stats.replied} cls="green" />
        {pg.stats.unmatched ? <Stat k="Not matched to a client" v={pg.stats.unmatched} s="Pick the client" cls="amber" /> : null}
      </div>
      <div className="bar">
        <Pills<MailFilter>
          options={[
            ['all', 'All'],
            ['needs', 'Needs us'],
            ['waiting', 'Waiting on client'],
            ['replied', 'Replied'],
            ...(pg.stats.unmatched || f === 'unmatched' ? ([['unmatched', 'Not matched', pg.stats.unmatched]] as [MailFilter, string, number][]) : []),
          ]}
          value={f}
          onChange={setF}
        />
      </div>
      {list.error ? (
        <div className="note bad" style={{ marginBottom: 12 }}>
          {errorText(list.error)}
        </div>
      ) : null}
      <MailTable
        threads={list.threads}
        filter={f}
        today={today}
        readOnly={readOnly}
        onOpen={(t) => setOpen({ kind: 'thread', id: t.id, snap: t })}
        onNotTask={(t) => setOpen({ kind: 'notask', thread: t })}
        connected={pg.my_gmail === 'connected'}
      />
      {list.more ? (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button className="btn" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
            {list.isFetchingNextPage ? 'Loading…' : 'Show more'}
          </button>
        </div>
      ) : null}

      {shownThread ? (
        <ThreadDrawer
          thread={shownThread}
          today={today}
          readOnly={readOnly}
          canAddDomains={can('client.emailDomains.write')}
          onClose={() => setOpen(null)}
          onNotTask={() => setOpen({ kind: 'notask', thread: shownThread })}
          onReview={(item) => setOpen({ kind: 'proposal', item, thread: shownThread })}
        />
      ) : null}
      {open?.kind === 'notask' ? <NotATaskModal thread={live(open.thread)} onClose={() => setOpen(null)} /> : null}
      {open?.kind === 'proposal' ? (
        <ProposalModal
          item={open.item}
          thread={live(open.thread)}
          canReassign={can('task.reassign')}
          onClose={() => setOpen(null)}
          onReadEmail={() => setOpen({ kind: 'thread', id: open.thread.id, snap: open.thread })}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------- Connect your email

function ConnectPanel({ state, readOnly }: { state: GoogleConnectionState; readOnly: boolean }) {
  const blocked = state === 'admin_blocked';
  const again = state === 'needs_reconnect' || state === 'revoked';
  return (
    <div className="panel" style={{ padding: '16px 18px', marginBottom: 14, display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{blocked ? 'Your email cannot be connected yet' : again ? 'Connect your email again' : 'Connect your email'}</div>
        <div className="sub" style={{ marginTop: 4, lineHeight: 1.5 }}>
          {blocked
            ? GOOGLE_CONNECT_ERROR_MESSAGE.admin_blocked
            : again
              ? 'Google stopped accepting the connection, so emails with your clients are not being read. Connect again to pick up where it left off.'
              : 'Ops roles connect their Gmail so replies and silence on their clients’ threads show here. Until you do, your clients have no email signals.'}
        </div>
        {!blocked ? (
          <ul className="sub" style={{ margin: '10px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
            <li>Read-only: we can never send, delete or change an email.</li>
            <li>Only threads with a client’s email domain are read; nothing else in your inbox.</li>
            <li>We keep the subject, who it was with and a short snippet — never the body.</li>
          </ul>
        ) : null}
      </div>
      {!blocked ? (
        readOnly ? (
          <span className="chip">Only they can connect</span>
        ) : (
          <a className="btn pri" href={gmailConnectHref()}>
            {again ? 'Connect again' : 'Connect Gmail'}
          </a>
        )
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- the table

function ClientLink({ client }: { client: ClientRef }) {
  return (
    <>
      <Link to={`/clients/book?client=${client.id}`} onClick={(e) => e.stopPropagation()} style={{ color: 'var(--brand)', fontWeight: 600, textDecoration: 'none' }}>
        {client.name}
      </Link>
      {client.priority === 'high' ? (
        <span className="pm" title="High priority client">
          {' '}▲
        </span>
      ) : null}
    </>
  );
}

function StateChip({ t }: { t: EmailThread }) {
  return (
    <span className={`chip ${STATE_CHIP[t.state]}`}>
      {EMAIL_STATE_LABEL[t.state]}
      {t.days_in_state ? ` ${t.days_in_state}d` : ''}
    </span>
  );
}

function DirChip({ t }: { t: EmailThread }) {
  return <span className={`chip ${t.direction === 'out' ? 'brand' : 'info'}`}>{t.direction === 'out' ? 'Sent' : 'Received'}</span>;
}

/** Prototype: a thread with no task and not yet replied offers "Make task"; an inbound one also "Not a task". */
const canAct = (t: EmailThread) => t.can_edit && !!t.client && !t.linked_task && !t.linked_escalation_id && !t.not_a_task && t.state !== 'replied';

function MailTable(props: {
  threads: EmailThread[];
  filter: MailFilter;
  today: string;
  readOnly: boolean;
  connected: boolean;
  onOpen: (t: EmailThread) => void;
  onNotTask: (t: EmailThread) => void;
}) {
  const { threads, today } = props;
  return (
    <div className="panel">
      <table>
        <thead>
          <tr>
            <th>Subject</th>
            <th>Client</th>
            <th>Direction</th>
            <th>Owner</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {threads.map((t) => (
            <tr key={t.id}>
              <td style={{ maxWidth: 380 }}>
                <a
                  href={`#${t.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    props.onOpen(t);
                  }}
                  style={{ color: 'inherit', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={t.subject}
                >
                  <b>{t.subject || '(No subject)'}</b>
                </a>
                <div className="sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.snippet}>
                  {t.snippet}
                </div>
                <div className="sub">
                  {whenLabel(t.last_message_at, today)}
                  {t.linked_task ? ` · Task: ${t.linked_task.title}` : t.linked_escalation_id ? ' · Raised as an escalation' : t.not_a_task ? ' · No task needed' : ''}
                  {t.pending_proposals ? (
                    <>
                      {' · '}
                      <span style={{ color: 'var(--brand)', fontWeight: 600 }}>{plural(t.pending_proposals, 'task')} found</span>
                    </>
                  ) : null}
                </div>
              </td>
              <td>
                {t.client ? (
                  <ClientLink client={t.client} />
                ) : (
                  <>
                    <span className="chip warm">Not matched</span>
                    <div className="sub" style={{ marginTop: 3 }}>
                      {t.candidate_clients.map((c) => c.name).join(' or ')}
                    </div>
                  </>
                )}
              </td>
              <td>
                <DirChip t={t} />
              </td>
              <td>{t.owner ? <Avatar user={t.owner} size={26} /> : <span className="sub">—</span>}</td>
              <td>
                <StateChip t={t} />
              </td>
              <td className="tr" style={{ whiteSpace: 'nowrap' }}>
                {props.readOnly ? null : !t.client && t.can_edit ? (
                  <button className="btn sm" onClick={() => props.onOpen(t)}>
                    Pick the client
                  </button>
                ) : canAct(t) ? (
                  <>
                    <MakeTaskButton thread={t} onOpen={() => props.onOpen(t)} />
                    {t.direction === 'in' ? (
                      <button className="btn sm ghost" onClick={() => props.onNotTask(t)}>
                        Not a task
                      </button>
                    ) : null}
                  </>
                ) : null}
              </td>
            </tr>
          ))}
          {!threads.length ? (
            <tr>
              <td colSpan={6}>
                <Empty title={props.filter === 'all' ? 'No emails' : 'No emails here'}>
                  {props.filter !== 'all'
                    ? 'Nothing in this view right now.'
                    : props.connected
                      ? 'Threads with your clients’ email domains appear here after the next read.'
                      : 'Connect your email above and threads with your clients appear here.'}
                </Empty>
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

/** Prototype `mailTask`: one click makes the follow-up task for the client's SPOC. With proposals found in the
 *  thread, the drawer opens instead so those are confirmed rather than duplicated. */
const taskTitle = (t: EmailThread) =>
  (t.direction === 'out' ? 'Follow up: ' : t.tone && (t.tone.label === 'escalate' || t.tone.label === 'negative' || t.tone.label === 'leave') ? 'Reply and fix: ' : 'Reply: ') +
  (t.subject || 'client email');

function MakeTaskButton({ thread, onOpen, primary, children }: { thread: EmailThread; onOpen?: () => void; primary?: boolean; children?: ReactNode }) {
  const make = useMakeThreadTask();
  const toast = useToast();
  const [key] = useState(newIdempotencyKey);
  const go = () => {
    if (thread.pending_proposals && onOpen) return onOpen();
    make.mutate(
      { id: thread.id, key, body: { kind: 'task', title: taskTitle(thread).slice(0, 200) } },
      {
        onSuccess: (r) => toast(r.task ? `Task made for ${firstName(r.task.assignee?.name ?? thread.owner?.name ?? 'the SPOC')}` : 'Raised as an escalation'),
        onError: (e) => toast(errorText(e)),
      },
    );
  };
  return (
    <button className={`btn sm${primary ? ' pri' : ''}`} onClick={go} disabled={make.isPending}>
      {make.isPending ? 'Making…' : children ?? 'Make task'}
    </button>
  );
}

// ---------------------------------------------------------------- the drawer (openMail)

function ToneBlock({ tone }: { tone: ThreadTone }) {
  if (!('reason' in tone)) return null;
  return (
    <div className="note warn" style={{ marginTop: 12 }}>
      <div style={{ fontWeight: 600 }}>
        {tone.chip}
        {tone.penalty ? <span className="mono" style={{ fontWeight: 500 }}>{` · ${tone.penalty > 0 ? '−' : '+'}${Math.abs(tone.penalty)} health`}</span> : null}
      </div>
      <div style={{ marginTop: 4 }}>{tone.reason}</div>
      {tone.quote ? <div style={{ marginTop: 6, fontStyle: 'italic' }}>“{tone.quote}”</div> : null}
    </div>
  );
}

function ThreadDrawer(props: {
  thread: EmailThread;
  today: string;
  readOnly: boolean;
  canAddDomains: boolean;
  onClose: () => void;
  onNotTask: () => void;
  onReview: (p: ProposedItem) => void;
}) {
  const t = props.thread;
  const proposals = useThreadProposals(t.id);
  const items = proposals.data?.data ?? [];
  const pending = items.filter((p) => p.status === 'pending');
  const act = canAct(t) && !props.readOnly;
  // Until the proposals have loaded (or if they failed), trust the thread's own count, so Make task never shows
  // beside proposals that would then be duplicated.
  const hasPending = proposals.data ? pending.length > 0 : !!t.pending_proposals;
  const [offer, setOffer] = useState<DomainOffer | null>(null);
  return (
    <Drawer
      title="Email"
      onClose={props.onClose}
      footer={
        <div className="split" style={{ width: '100%' }}>
          {act && !hasPending ? (
            <MakeTaskButton thread={t} primary>
              Make task
            </MakeTaskButton>
          ) : t.linked_task || t.linked_escalation_id ? (
            <button className="btn pri" disabled>
              Task already made
            </button>
          ) : null}
          {act && t.direction === 'in' ? (
            <button className="btn ghost" onClick={props.onNotTask}>
              Not a task
            </button>
          ) : null}
          <div className="grow" />
          {t.gmail_url ? (
            <a className="btn" href={t.gmail_url} target="_blank" rel="noreferrer noopener">
              Open in Gmail ↗
            </a>
          ) : null}
          <button className="btn" onClick={props.onClose}>
            Close
          </button>
        </div>
      }
    >
      <h2 style={{ fontSize: 18, marginBottom: 6 }}>{t.subject || '(No subject)'}</h2>
      <div className="sub" style={{ marginBottom: 12 }}>
        {t.direction === 'in' ? 'From the client' : 'From us'}, {whenWithTime(t.last_message_at)}. {t.client ? <ClientLink client={t.client} /> : null}
        {t.owner ? <> · Owner {t.owner.name}</> : null} · {plural(t.message_count, 'message')}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {t.tone ? <span className={`chip ${TONE_CHIP[t.tone.label] ?? ''}`}>{t.tone.chip}</span> : null}
        <StateChip t={t} />
        <DirChip t={t} />
      </div>
      <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.55, border: '1px solid var(--line)', borderRadius: 10, padding: 14, background: 'var(--card, var(--surface))' }}>
        {t.snippet || '(No text was captured for this email.)'}
      </div>
      <div className="sub" style={{ marginTop: 6 }}>
        The latest message, cut to a short snippet. The full email stays in Gmail.
      </div>
      {t.tone ? <ToneBlock tone={t.tone} /> : null}

      {offer ? (
        <OfferBlock offer={offer} onDone={() => setOffer(null)} />
      ) : !t.client ? (
        <AssignBlock thread={t} readOnly={props.readOnly} onOffer={props.canAddDomains ? setOffer : undefined} />
      ) : null}

      <div className="lbl" style={{ margin: '18px 0 8px' }}>
        What we found in it
      </div>
      {proposals.isLoading ? (
        <Loading />
      ) : proposals.error ? (
        <ErrorState error={proposals.error} onRetry={() => void proposals.refetch()} />
      ) : items.length ? (
        <div style={{ border: '1px solid var(--line)', borderRadius: 10 }}>
          {items.map((p) => (
            <div className="li" key={p.id}>
              <div className="b">
                <div className="t" style={{ fontWeight: 500 }}>
                  {p.title}
                </div>
                <div className="m">
                  <span>{p.kind === 'esc' ? 'Escalation' : 'Task'}</span>
                  {p.suggested_due ? <span>Due {whenLabel(`${p.suggested_due}T12:00:00+05:30`, props.today)}</span> : null}
                  {p.suggested_assignee ? <span>For {firstName(p.suggested_assignee.name)}</span> : null}
                  {p.status === 'pending' ? (
                    p.is_late ? <span className="chip cold">Waiting {p.waited_hours}h</span> : <span>Proposed {p.waited_hours}h ago</span>
                  ) : (
                    <span className={`chip ${p.status === 'confirmed' ? 'hot' : ''}`}>{p.status === 'confirmed' ? 'Confirmed' : 'Rejected'}</span>
                  )}
                </div>
                <div className="sub" style={{ marginTop: 4, fontStyle: 'italic' }}>
                  “{p.quote}”
                </div>
              </div>
              {p.status === 'pending' && p.can_decide && !props.readOnly ? (
                <button className="btn sm" onClick={() => props.onReview(p)}>
                  Review
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="sub">Nothing that needs a task.</div>
      )}
      <div className="note" style={{ marginTop: 14 }}>
        Read-only: only threads with a client’s email domain are read, and never the message body.
      </div>
    </Drawer>
  );
}

type DomainOffer = { client: ClientRef; domains: string[] };

/** Unmatched: pick one of the candidate clients. The response's domain_offer goes up to the drawer (managers). */
function AssignBlock({ thread, readOnly, onOffer }: { thread: EmailThread; readOnly: boolean; onOffer?: (o: DomainOffer) => void }) {
  const [pick, setPick] = useState(thread.candidate_clients[0]?.id ?? '');
  const update = useUpdateThread();
  const toast = useToast();
  const assign = () =>
    update.mutate(
      { thread, body: { client_id: pick } },
      {
        onSuccess: (r) => {
          const c = thread.candidate_clients.find((x) => x.id === pick)!;
          toast(`Assigned to ${c.name}`);
          if (r.domain_offer.length && onOffer) onOffer({ client: c, domains: r.domain_offer });
        },
        onError: (e) => toast(errorText(e)),
      },
    );
  return (
    <div className="note warn" style={{ marginTop: 14 }}>
      <div style={{ fontWeight: 600 }}>Which client is this?</div>
      <div style={{ marginTop: 4 }}>Its email domains belong to more than one client ({thread.participant_domains.join(', ')}). Pick the one it is about.</div>
      {thread.can_edit && !readOnly ? (
        <div className="split" style={{ marginTop: 10, flexWrap: 'wrap' }}>
          <select className="in" style={{ width: 'auto' }} value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Client">
            {thread.candidate_clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <button className="btn sm pri" onClick={assign} disabled={!pick || update.isPending}>
            {update.isPending ? 'Saving…' : 'Assign'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** After assigning: add the thread's other domains to that client, so the next email matches by itself. */
function OfferBlock({ offer, onDone }: { offer: DomainOffer; onDone: () => void }) {
  const add = useAddDomains();
  const toast = useToast();
  return (
    <div className="note" style={{ marginTop: 14 }}>
      <div>
        Add <b>{offer.domains.join(', ')}</b> to {offer.client.name}, so the next email from there matches by itself?
      </div>
      <div className="split" style={{ marginTop: 10 }}>
        <button
          className="btn sm pri"
          disabled={add.isPending}
          onClick={() =>
            add.mutate(
              { clientId: offer.client.id, domains: offer.domains },
              {
                onSuccess: (r) => {
                  toast(
                    r.failed.length
                      ? `${r.failed.map((f) => `Could not add ${f.domain}: ${errorText(f.error)}`).join(' ')}${r.added.length ? ` Added ${r.added.join(', ')}.` : ''}`
                      : `Linked to ${offer.client.name}`,
                  );
                  onDone();
                },
              },
            )
          }
        >
          {add.isPending ? 'Adding…' : `Add ${offer.domains.length === 1 ? 'it' : 'them'}`}
        </button>
        <button className="btn sm" onClick={onDone}>
          Not now
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Not a task (whyModal) and reject

const REASONS = Object.entries(DROP_REASON_LABEL) as [DropReason, string][];

function WhyModal(props: { title: string; what: string; onClose: () => void; pending: boolean; errs: Record<string, string>; onSave: (r: DropReason, note: string) => void }) {
  const [reason, setReason] = useState<DropReason>(REASONS[0]![0]);
  const [note, setNote] = useState('');
  const [local, setLocal] = useState('');
  return (
    <Modal
      title={props.title}
      sub={
        <>
          “{props.what}”
          <br />
          The Ops associate director sees what was left out and why.
        </>
      }
      onClose={props.onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={props.onClose}>
            Cancel
          </button>
          <button
            className="btn bad"
            disabled={props.pending}
            onClick={() => {
              if (reason === 'other' && !note.trim()) return setLocal('Add a note when the reason is Other');
              setLocal('');
              props.onSave(reason, note.trim());
            }}
          >
            Leave it out
          </button>
        </>
      }
    >
      <Field label="Why" error={props.errs['not_a_task.reason_code'] ?? props.errs.reason_code}>
        <select className="in" value={reason} onChange={(e) => setReason(e.target.value as DropReason)}>
          {REASONS.map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Note (needed if Other)" error={local || props.errs['not_a_task.note'] || props.errs.note}>
        <input className="in" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}

function NotATaskModal({ thread, onClose }: { thread: EmailThread; onClose: () => void }) {
  const update = useUpdateThread();
  const { errs, fail, toast } = useFail(['not_a_task.reason_code', 'not_a_task.note']);
  return (
    <WhyModal
      title="This email needs no task"
      what={thread.subject}
      onClose={onClose}
      pending={update.isPending}
      errs={errs}
      onSave={(reason_code, note) =>
        update.mutate(
          { thread, body: { not_a_task: { reason_code, note: note || undefined } } },
          {
            onSuccess: () => {
              toast('Marked as no task, and logged.');
              onClose();
            },
            onError: fail,
          },
        )
      }
    />
  );
}

// ---------------------------------------------------------------- a proposal (propReview / propConfirm / propReject)

function ProposalModal(props: { item: ProposedItem; thread: EmailThread; canReassign: boolean; onClose: () => void; onReadEmail: () => void }) {
  const p = props.item;
  const [title, setTitle] = useState(p.title);
  const [due, setDue] = useState(p.suggested_due ?? '');
  const [who, setWho] = useState(p.suggested_assignee?.id ?? '');
  const [rejecting, setRejecting] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const users = useUsers(props.canReassign);
  const confirm = useConfirmProposal();
  const reject = useRejectProposal();
  const { errs, fail, toast } = useFail(['title', 'due', 'assignee_id']);
  const rej = useFail(['reason_code', 'note']);
  const people = useMemo(
    () => (users.data ?? []).filter((u) => u.status === 'active' && (u.role === 'ops_ad' || u.role === 'ops_member')),
    [users.data],
  );
  const esc = p.kind === 'esc';

  if (rejecting)
    return (
      <WhyModal
        title="Reject this task"
        what={p.title}
        onClose={props.onClose}
        pending={reject.isPending}
        errs={rej.errs}
        onSave={(reason_code, note) =>
          reject.mutate(
            { id: p.id, body: { reason_code, note: note || undefined } },
            {
              onSuccess: () => {
                toast('Rejected and logged in Dropped tasks.');
                props.onClose();
              },
              onError: rej.fail,
            },
          )
        }
      />
    );

  const save = () => {
    if (title.trim().length < 3) return toast('Add a title');
    confirm.mutate(
      {
        id: p.id,
        key,
        body: {
          title: title.trim() !== p.title ? title.trim() : undefined,
          due: !esc && due && due !== p.suggested_due ? due : undefined,
          assignee_id: !esc && props.canReassign && who && who !== p.suggested_assignee?.id ? who : undefined,
        },
      },
      {
        onSuccess: (r) => {
          toast(r.escalation ? 'Confirmed. It is now an escalation.' : `Confirmed. It is now a task for ${firstName(r.task?.assignee?.name ?? p.suggested_assignee?.name ?? 'the SPOC')}.`);
          props.onClose();
        },
        onError: fail,
      },
    );
  };

  return (
    <Modal
      title="Proposed from an email"
      sub={
        <>
          {p.client.name}. {p.source.subject}, {whenLabel(p.source.at, todayIso())}.{' '}
          {p.is_late ? <span className="chip cold">Waiting {p.waited_hours}h</span> : `Proposed ${p.waited_hours}h ago.`}
        </>
      }
      onClose={props.onClose}
      footer={
        <>
          <button className="btn bad" onClick={() => setRejecting(true)}>
            Reject
          </button>
          <div className="grow" />
          <button className="btn" onClick={props.onClose}>
            Later
          </button>
          <button className="btn good" onClick={save} disabled={confirm.isPending}>
            {confirm.isPending ? 'Confirming…' : 'Confirm'}
          </button>
        </>
      }
    >
      <div className="note" style={{ marginBottom: 12 }}>
        From the email: “{p.quote}”{' '}
        <a
          href="#email"
          onClick={(e) => {
            e.preventDefault();
            props.onReadEmail();
          }}
          style={{ color: 'var(--brand)', fontWeight: 600, marginLeft: 6 }}
        >
          See the email
        </a>
      </div>
      <Field label={esc ? 'Escalation' : 'Task'} error={errs.title}>
        <input className="in" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      {esc ? (
        <div className="sub">Will be raised as an escalation for the client’s SPOC.</div>
      ) : (
        <div className="cols c2">
          <Field label="Due" error={errs.due}>
            <input className="in" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </Field>
          <Field label={props.canReassign ? 'Who' : 'Who (client’s SPOC)'} error={errs.assignee_id}>
            {props.canReassign ? (
              <select className="in" value={who} onChange={(e) => setWho(e.target.value)}>
                {p.suggested_assignee && !people.some((u) => u.id === p.suggested_assignee!.id) ? (
                  <option value={p.suggested_assignee.id}>{p.suggested_assignee.name}</option>
                ) : null}
                {people.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            ) : (
              <input className="in" disabled value={p.suggested_assignee?.name ?? 'The client’s SPOC'} />
            )}
          </Field>
        </div>
      )}
    </Modal>
  );
}

